import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, formatDerivedAccountLevel, type PhoneMetrics } from "../renderConstants";
import { getCharacterById, resolveCharacterImagePath } from "../../economy/characters";
import { rankedTierForRating } from "../../versus/ranked";

// ── Dependency interface ────────────────────────────────────────────

export interface PhoneFriendDetailDeps {
  drawCircularPortrait(path: string, x: number, y: number, size: number): void;
  rafsbyPortrait: HTMLImageElement | null;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneFriendDetailScreen(
  deps: PhoneFriendDetailDeps,
  ctx: CanvasRenderingContext2D,
  _model: RenderModel,
  uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
  friend: { odId: string; username: string; level: number; online: boolean; equippedSkin: string; isNpc?: boolean; versusWins?: number; versusLosses?: number; versusElo?: number },
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;

  // Header
  ctx.fillStyle = "#64d8ff";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("FRIEND", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  // Divider
  ctx.fillStyle = "#2a3a55";
  for (let dx = contentX; dx < contentRight; dx += 4) {
    ctx.fillRect(dx, curY, 2, 1);
  }
  curY += metrics.sectionGap;

  // Portrait + name row (like message detail sender info)
  const portraitSize = desktop ? 36 : 30;
  const pX = contentX;
  const pY = curY;
  const charDef = getCharacterById(friend.equippedSkin);

  if (friend.isNpc && deps.rafsbyPortrait) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(pX + portraitSize / 2, pY + portraitSize / 2, portraitSize / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.translate(pX + portraitSize, pY);
    ctx.scale(-1, 1);
    ctx.drawImage(deps.rafsbyPortrait, 0, 0, portraitSize, portraitSize);
    ctx.restore();
  } else {
    const imgPath = resolveCharacterImagePath(friend.equippedSkin);
    deps.drawCircularPortrait(imgPath, pX, pY, portraitSize);
  }

  // Online indicator on portrait
  const dotX = pX + portraitSize - 3;
  const dotY = pY + portraitSize - 3;
  const dotR = desktop ? 4 : 3;
  ctx.fillStyle = "#0a1020";
  ctx.beginPath();
  ctx.arc(dotX, dotY, dotR + 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = friend.online ? "#4dff88" : "#3a4a6a";
  ctx.beginPath();
  ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2);
  ctx.fill();

  // Username + status beside portrait
  const textX = pX + portraitSize + 10;
  ctx.fillStyle = "#c8d8f0";
  ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(friend.username.toUpperCase(), textX, pY + 2);

  ctx.fillStyle = friend.online ? "#4dff88" : "#5a7a9a";
  ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
  ctx.fillText(friend.online ? "ONLINE" : "OFFLINE", textX, pY + (desktop ? 14 : 13));

  curY += portraitSize + metrics.sectionGap;

  // Character name
  if (charDef) {
    ctx.fillStyle = "#7fa0c2";
    ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.fillText(charDef.name.toUpperCase(), contentX, curY);
    curY += desktop ? 12 : 10;
  }
  curY += 4;

  // ── Stats rows ──
  const statRowH = desktop ? 26 : 24;
  const statGap = 4;

  const stats: Array<{ label: string; value: string; color: string }> = [];

  if (friend.isNpc) {
    stats.push({ label: "ROLE", value: "GUIDE", color: "#a78bfa" });
  } else {
    stats.push({ label: "LEVEL", value: formatDerivedAccountLevel(friend.level, ""), color: "#ffd700" });

    const wins = friend.versusWins ?? 0;
    const losses = friend.versusLosses ?? 0;
    const total = wins + losses;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
    const elo = friend.versusElo ?? 0;
    const tier = elo > 0 ? rankedTierForRating(elo) : null;

    if (tier) {
      stats.push({ label: "RANK", value: tier.toUpperCase(), color: "#ff6b9d" });
    }
    stats.push({ label: "WINS", value: `${wins}`, color: "#4dff88" });
    stats.push({ label: "LOSSES", value: `${losses}`, color: "#ff6b6b" });
    if (total > 0) {
      stats.push({ label: "WIN RATE", value: `${winRate}%`, color: "#64d8ff" });
    }
  }

  for (const stat of stats) {
    ctx.fillStyle = "rgba(22,32,48,0.95)";
    ctx.fillRect(contentX, curY, contentW, statRowH);
    // Accent bar on left
    ctx.fillStyle = stat.color + "44";
    ctx.fillRect(contentX, curY, 3, statRowH);

    ctx.fillStyle = "#7fa0c2";
    ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(stat.label, contentX + 10, curY + statRowH / 2);

    ctx.fillStyle = stat.color;
    ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "right";
    ctx.fillText(stat.value, contentRight - 8, curY + statRowH / 2);

    curY += statRowH + statGap;
  }

  // ── Action buttons at bottom (pinned) ──
  const btnH = metrics.ctaH;
  const btnGap = 4;

  if (friend.isNpc) {
    // BACK — full width at very bottom
    const backY = scrY + scrH - btnH - 6;
    const backRect: Rect = { x: contentX, y: backY, w: contentW, h: btnH };
    uiRects.friendDetailBack = backRect;
    ctx.fillStyle = "#162030";
    ctx.fillRect(backRect.x, backRect.y, backRect.w, backRect.h);
    ctx.strokeStyle = "#5a7a9a";
    ctx.lineWidth = 1;
    ctx.strokeRect(backRect.x, backRect.y, backRect.w, backRect.h);
    ctx.fillStyle = "#8a9ab0";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);

    // HOW TO PLAY + WORLD TOUR side by side above BACK
    const pairY = backY - btnH - btnGap;
    const halfW = Math.floor((contentW - btnGap) / 2);

    const wtRect: Rect = { x: contentX, y: pairY, w: halfW, h: btnH };
    uiRects.rafsbyWorldTour = wtRect;
    ctx.fillStyle = "#0a2010";
    ctx.fillRect(wtRect.x, wtRect.y, wtRect.w, wtRect.h);
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 1;
    ctx.strokeRect(wtRect.x, wtRect.y, wtRect.w, wtRect.h);
    ctx.fillStyle = "#22c55e";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("HELP?", wtRect.x + wtRect.w / 2, wtRect.y + wtRect.h / 2);

    const htpRect: Rect = { x: contentX + halfW + btnGap, y: pairY, w: halfW, h: btnH };
    uiRects.rafsbyHowToPlay = htpRect;
    ctx.fillStyle = "#0a2028";
    ctx.fillRect(htpRect.x, htpRect.y, htpRect.w, htpRect.h);
    ctx.strokeStyle = "#22d3ee";
    ctx.lineWidth = 1;
    ctx.strokeRect(htpRect.x, htpRect.y, htpRect.w, htpRect.h);
    ctx.fillStyle = "#22d3ee";
    ctx.fillText("HOW TO PLAY", htpRect.x + htpRect.w / 2, htpRect.y + htpRect.h / 2);
  } else {
    // BACK — full width at very bottom
    const backY = scrY + scrH - btnH - 6;
    const backRect: Rect = { x: contentX, y: backY, w: contentW, h: btnH };
    uiRects.friendDetailBack = backRect;
    ctx.fillStyle = "#162030";
    ctx.fillRect(backRect.x, backRect.y, backRect.w, backRect.h);
    ctx.strokeStyle = "#5a7a9a";
    ctx.lineWidth = 1;
    ctx.strokeRect(backRect.x, backRect.y, backRect.w, backRect.h);
    ctx.fillStyle = "#8a9ab0";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);

    // BATTLE + REMOVE side by side above BACK
    const pairY = backY - btnH - btnGap;
    const halfW = Math.floor((contentW - btnGap) / 2);

    if (friend.online) {
      const battleRect: Rect = { x: contentX, y: pairY, w: halfW, h: btnH };
      uiRects.friendDetailBattle = battleRect;
      ctx.fillStyle = "#2a1020";
      ctx.fillRect(battleRect.x, battleRect.y, battleRect.w, battleRect.h);
      ctx.strokeStyle = "#ff4466";
      ctx.lineWidth = 1;
      ctx.strokeRect(battleRect.x, battleRect.y, battleRect.w, battleRect.h);
      ctx.fillStyle = "#ff4466";
      ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("BATTLE", battleRect.x + battleRect.w / 2, battleRect.y + battleRect.h / 2);

      const removeRect: Rect = { x: contentX + halfW + btnGap, y: pairY, w: halfW, h: btnH };
      uiRects.friendDetailRemove = removeRect;
      ctx.fillStyle = "#2a1010";
      ctx.fillRect(removeRect.x, removeRect.y, removeRect.w, removeRect.h);
      ctx.strokeStyle = "#ff6644";
      ctx.lineWidth = 1;
      ctx.strokeRect(removeRect.x, removeRect.y, removeRect.w, removeRect.h);
      ctx.fillStyle = "#ff6644";
      ctx.fillText("REMOVE", removeRect.x + removeRect.w / 2, removeRect.y + removeRect.h / 2);
    } else {
      // Offline: only REMOVE full width above BACK
      const removeRect: Rect = { x: contentX, y: pairY, w: contentW, h: btnH };
      uiRects.friendDetailRemove = removeRect;
      ctx.fillStyle = "#2a1010";
      ctx.fillRect(removeRect.x, removeRect.y, removeRect.w, removeRect.h);
      ctx.strokeStyle = "#ff6644";
      ctx.lineWidth = 1;
      ctx.strokeRect(removeRect.x, removeRect.y, removeRect.w, removeRect.h);
      ctx.fillStyle = "#ff6644";
      ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("REMOVE FRIEND", removeRect.x + removeRect.w / 2, removeRect.y + removeRect.h / 2);
    }
  }
}
